import React, { ChangeEvent, useContext, useState } from 'react';

import FilterPlatesContext from 'contexts/FilterPlatesContext';
import ReportContext from 'contexts/ReportContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import aguid from 'lib/aguid';
import STORE from 'store';
import withoutNulls from 'api/expression/withoutNulls';
import NewQuickFilterForm from '../../CustomerLaneCommitsBoard/Settings/QuickFilters/NewQuickFilterForm/NewQuickFilterForm';

const NewReportQuickFilterFormContainer = ({
  close,
  quickFilter,
}: {
  close: () => void;
  quickFilter?: LegacySavedFilter;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { dataTypes } = useContext(FilterPlatesContext);
  const { report } = useContext(ReportContext);
  const isEditing = quickFilter !== undefined;
  const [name, setName] = useState<string>(quickFilter?.name ?? '');
  const [drillDowns, setDrillDowns] = useState<FilterPlateType[]>(
    quickFilter?.drillDowns ?? [],
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const isValid = drillDowns.length !== 0;

  const onSave = () => {
    if (!isValid || !report) {
      return;
    }

    const filter: LegacySavedFilter = {
      version: '2',
      id: quickFilter ? quickFilter.id : aguid(), // editing overwrites the same doc
      scope: [], // report quick filters are not scoped to specific entities
      drillDowns,
      source: `report: ${report.id}`, // namespaced so useQuickFilters can query only this report's filters
      name,
      dateField: 'date',
    };

    setIsSaving(true);
    STORE.filterDefinitions
      .getLegacySavedFiltersRef({ accountId: selectedAccountId })
      .doc(filter.id)
      .set(filter)
      .then(() => {
        const currentOrder = report.quickFilterOrder || [];
        // Editing keeps the same id, so the order (and report doc) is unchanged —
        // only append for a brand-new filter not already in the order.
        if (currentOrder.includes(filter.id)) {
          setIsSaving(false);
          close();
          return;
        }
        const newReport: ReportType = {
          ...report,
          quickFilterOrder: [...currentOrder, filter.id],
        };
        STORE.getReportsRef({ accountId: selectedAccountId })
          .doc(report.id)
          .set(withoutNulls(newReport))
          .then(() => {
            setIsSaving(false);
            close();
          });
      });
  };

  return (
    <FilterPlatesProvider
      drillDowns={drillDowns}
      setDrillDowns={setDrillDowns}
      dataTypes={dataTypes}
    >
      <NewQuickFilterForm
        name={name}
        onNameChanged={onNameChanged}
        close={close}
        onSave={onSave}
        isSaving={isSaving}
        isValid={isValid}
        title={isEditing ? 'Edit Quick Filter' : 'New Quick Filter'}
        saveLabel={isEditing ? 'Save Changes' : 'Save'}
      />
    </FilterPlatesProvider>
  );
};

export default NewReportQuickFilterFormContainer;
